import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const STORAGE_KEY = 'summer-squad-theme';
const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
  setTheme: () => {},
});

// Check if we're in production
const isProduction = typeof import.meta !== 'undefined' && import.meta.env && 
  (import.meta.env.VITE_APP_ENV === 'production' || import.meta.env.VITE_APP_ENV === 'staging');

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    // Force light mode in production
    if (isProduction) return 'light';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'dark' ? 'dark' : 'light';
  });

  // Override setTheme to prevent dark mode in production
  const setTheme = useCallback((newTheme) => {
    if (isProduction) {
      // Silently ignore dark mode attempts in production
      if (newTheme === 'dark' || (typeof newTheme === 'function' && newTheme(theme) === 'dark')) {
        return;
      }
    }
    if (typeof newTheme === 'function') {
      setThemeState(newTheme);
    } else {
      setThemeState(newTheme);
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    if (isProduction) {
      // In production, always keep it light
      return;
    }
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Force light mode in production
    const effectiveTheme = isProduction ? 'light' : theme;
    if (!isProduction) {
      window.localStorage.setItem(STORAGE_KEY, theme);
    }
    document.documentElement.dataset.navTheme = effectiveTheme;
  }, [theme]);

  const value = useMemo(
    () => ({
      theme: isProduction ? 'light' : theme, // Always return 'light' in production
      setTheme,
      toggleTheme,
    }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useThemeSettings = () => useContext(ThemeContext);


